import React, { useState } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Grid, useMediaQuery, useTheme } from '@mui/material';
import CommonInput from '../common/input/InputBox';
import CommonButton from '../common/button/Button';
import axios from "axios";
import useSnackbarAlert from "../../utils/snackbarHandlers";
import { useNavigate } from "react-router-dom";
import "./lotterySystem.css";
import CommonDropDown from '../common/dropdown/CommonDropdown';

interface ContactDetails {
  name: string;
  email: string;
  phoneNumber: string;
  subject: string;
  message: string;
  status: string,
  businessName: string,
};

const initialData: ContactDetails = {
  name: "",
  email: "",
  phoneNumber: "",
  subject: "",
  message: "",
  status: "",
  businessName: "",
};

interface GetInTouchFormProps {
  contactUsData?: any;
  edit?: boolean;
  formSubmit?: (value: ContactDetails) => void
}

const GetInTouchForm: React.FC<GetInTouchFormProps> = ({ contactUsData = initialData, edit = false, formSubmit }) => {
  const { t } = useTranslation('translation');
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState<boolean>(false);
  //@ts-ignore
  const config = window.config;
  const apiBaseURL = config?.server || "http://18.217.95.31:8080";
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .required(t('fullnameRequired')),
    email: yup
      .string()
      .required(t('emailRequired')),
    phoneNumber: yup
      .string()
      .required(t('phoneNumberRequired')),
    subject: yup.string(),
    message: yup.string(),
    businessName: yup.string(),
  });

  const onSubmit = (value: ContactDetails) => {
    const fileHeaders = {
      accept: "application/json",
      "Accept-Language": "en-US,en;q=0.8",
      "Content-Type": "application/json",
    };
    const body = {
      name: value.name,
      email: value.email,
      phoneNumber: value.phoneNumber,
      subject: value.subject,
      message: value.message,
      status: "NEW",
      businessName: value.businessName
    };

    axios
      .post(`${apiBaseURL}/api/contact/addContact`, body, { headers: fileHeaders })
      .then((data: any) => {
        if (data?.status === 200) {
          showSuccessMessage(t("contactCreatedSuccessfully", "Contact created successfully"))
        }
      })
      .catch((err: any) =>
        showErrorMessage(t("somethingWentWrong"))
      )
      .finally(() => {
        setLoading(false);
        resetForm();
      });
  }

  const formik = useFormik({
    initialValues: edit ? contactUsData : initialData,
    onSubmit: onSubmit,
    validationSchema: validationSchema,
    enableReinitialize: true,
  });

  const { values, errors, touched, handleSubmit, handleChange, resetForm } = formik;

  const options: any = [
    { value: "NEW", key: "New" },
    { value: "RESOLVED", key: "Resolved" },
    { value: "ISSUE", key: "Issue" },
  ];

  return (
    <>
      <Grid container spacing={2} direction={isMobileOrTablet ? 'column' : 'row'}>
        <Grid item xs={12} md={6}>
          <CommonInput
            id="full-name-input-box"
            fullWidth
            name="name"
            className="landing-input-box"
            label={t("fullName")}
            placeholder={t('fullnamePlaceholder')}
            onChange={handleChange}
            disabled={edit}
            value={values?.name}
            error={touched?.name && errors?.name ? true : false}
            helperText={errors?.name && touched?.name ? errors?.name : ""}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CommonInput
            id="email-id-input-box"
            fullWidth
            name="email"
            className="landing-input-box"
            label={t("email")}
            placeholder={t('emailAddressPlaceholder')}
            onChange={handleChange}
            disabled={edit}
            value={values?.email}
            error={touched?.email && errors?.email ? true : false}
            helperText={errors?.email && touched?.email ? errors?.email : ""}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} direction={isMobileOrTablet ? 'column' : 'row'}>
        <Grid item xs={12} md={6}>
          <CommonInput
            id="phone-number-input-box"
            fullWidth
            name="phoneNumber"
            className="landing-input-box"
            label={t("phone")}
            placeholder={t('mobilePlaceholder')}
            onChange={handleChange}
            disabled={edit}
            value={values?.phoneNumber}
            error={touched?.phoneNumber && errors?.phoneNumber ? true : false}
            helperText={errors?.phoneNumber && touched?.phoneNumber ? errors?.phoneNumber : ""}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CommonInput
            id="subject-input-box"
            fullWidth
            name="subject"
            className="landing-input-box"
            label={t("subject")}
            placeholder={t('subjectPlaceholder')}
            onChange={handleChange}
            disabled={edit}
            value={values?.subject}
            error={touched?.subject && errors?.subject ? true : false}
            helperText={errors?.subject && touched?.subject ? errors?.subject : ""}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} direction={isMobileOrTablet ? 'column' : 'row'}>
        {edit && <Grid item xs={12} md={6}>
          <CommonDropDown
            id="status-input-box"
            fullWidth
            name="status"
            label={t("status")}
            onChange={handleChange}
            value={values?.status}
            error={touched?.status && errors?.status ? true : false}
            helperText={errors?.status && touched?.status ? errors?.status : ""}
            options={options}
            sx={{ width: "60%" }}
          />
        </Grid>}

        <Grid item xs={12} md={6}>
          <CommonInput
            id="business-name-input-box"
            fullWidth
            name="businessName"
            className="landing-input-box"
            label={t("businessName")}
            placeholder={t('businessNamePlaceholder')}
            onChange={handleChange}
            disabled={edit}
            value={values?.businessName}
            error={touched?.businessName && errors?.businessName ? true : false}
            helperText={errors?.businessName && touched?.businessName ? errors?.businessName : ""}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CommonInput
            id="message-input-box"
            fullWidth
            name="message"
            className="landing-input-message-box"
            label={t("message")}
            placeholder={t('messagePlaceholder')}
            minRows={4}
            maxRows={8}
            multiline
            onChange={handleChange}
            value={values?.message}
            error={touched?.message && errors?.message ? true : false}
            helperText={errors?.message && touched?.message ? errors?.message : ""}
          />
        </Grid>
      </Grid>
      <Grid>
        {!edit && <CommonButton
          id="login-button"
          className="landing-send-message-button"
          variant="contained"
          disabled={loading}
          onClick={() => {
            handleSubmit();
          }}
        >
          {t('sendMessage')}
          {loading && (
            <CircularProgress
              color="inherit"
              size={14}
              thickness={6}
              style={{ marginLeft: "8px" }}
            />
          )}
        </CommonButton>}

        {edit && <button
          id="contact-update-button"
          className="contact-update-button"
          disabled={loading}
          onClick={() => formSubmit && formSubmit(values)}
        >
          {t('update')}
          {loading && (
            <CircularProgress
              color="inherit"
              size={14}
              thickness={6}
              style={{ marginLeft: "8px" }}
            />
          )}
        </button>}
      </Grid>
    </>
  )
}

export default GetInTouchForm;